import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Button,
  Avatar,
  Chip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from '../contexts/AuthContext';

interface NavbarProps {
  onLoginClick?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onLoginClick }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { currentUser, userProfile, logout } = useAuth();
  const theme = useTheme();

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleScrollTo = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    handleClose();
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleClose();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const navItems = [
    { label: 'How It Works', id: 'how-it-works' },
    { label: 'Pricing', id: 'pricing' },
    { label: 'Devices', id: 'devices' },
    { label: 'FAQ', id: 'faq' },
    { label: 'About', id: 'about' },
    { label: 'Contact', id: 'contact' },
  ];

  // Get display name from user profile or fallback to Firebase user
  const getDisplayName = () => {
    if (userProfile?.display_name) return userProfile.display_name;
    if (currentUser?.displayName) return currentUser.displayName;
    if (userProfile?.email) return userProfile.email;
    if (currentUser?.email) return currentUser.email;
    return 'User';
  };

  // Get subscription tier color
  const getSubscriptionColor = (tier?: string) => {
    switch (tier) {
      case 'pro':
        return 'warning';
      case 'enterprise':
        return 'error';
      case 'free':
      default:
        return 'default';
    }
  };

  return (
    <AppBar position="fixed" color="default" elevation={2}>
      <Toolbar>
        <Typography
          variant="h6"
          component="div"
          sx={{ flexGrow: 1, fontWeight: 700, cursor: 'pointer' }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          Field
          <span style={{ color: theme.palette.primary.main }}>Sense</span> AI
        </Typography>

        {/* Desktop Navigation */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
          {navItems.map((item) => (
            <Button
              key={item.id}
              color="inherit"
              onClick={() => handleScrollTo(item.id)}
              sx={{ textTransform: 'none' }}
            >
              {item.label}
            </Button>
          ))}
          
          {/* Auth Section */}
          {currentUser ? (
            <Box display="flex" alignItems="center" gap={1}>
              <Avatar 
                sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}
                src={userProfile?.photo_url || currentUser.photoURL || undefined}
              >
                {getDisplayName()[0]?.toUpperCase() || 'U'}
              </Avatar>
              
              {/* Show subscription tier badge */}
              {userProfile?.subscription_tier && userProfile.subscription_tier !== 'free' && (
                <Chip
                  label={userProfile.subscription_tier.toUpperCase()}
                  size="small"
                  color={getSubscriptionColor(userProfile.subscription_tier)}
                  sx={{ ml: 1, fontSize: '0.7rem', height: 20 }}
                />
              )}
              
              <Button
                color="inherit"
                onClick={handleLogout}
                sx={{ textTransform: 'none' }}
              >
                Logout
              </Button>
            </Box>
          ) : (
            <Button
              variant="contained"
              onClick={onLoginClick}
              sx={{ textTransform: 'none' }}
            >
              Login
            </Button>
          )}
        </Box>

        {/* Mobile Navigation */}
        <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
          <IconButton
            size="large"
            edge="end"
            color="inherit"
            aria-label="menu"
            onClick={handleMenu}
          >
            <MenuIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            {navItems.map((item) => (
              <MenuItem key={item.id} onClick={() => handleScrollTo(item.id)}>
                {item.label}
              </MenuItem>
            ))}
            {currentUser ? (
              <>
                <MenuItem disabled>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar 
                      sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}
                      src={userProfile?.photo_url || currentUser.photoURL || undefined}
                    >
                      {getDisplayName()[0]?.toUpperCase() || 'U'}
                    </Avatar>
                    <Box>
                      <Typography variant="body2">
                        {getDisplayName()}
                      </Typography>
                      {userProfile?.subscription_tier && (
                        <Typography variant="caption" color="text.secondary">
                          {userProfile.subscription_tier} plan
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  Logout
                </MenuItem>
              </>
            ) : (
              <MenuItem onClick={onLoginClick}>
                Login
              </MenuItem>
            )}
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;